import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import FileList from '../components/files/FileList'
import Comments from '../components/files/Comments'
import DRLProgressBars from '../components/etapa0/DRLProgressBars'
import {
  Briefcase, Target, Compass, Activity, FileText, MessageSquare,
  TrendingUp, CheckCircle2, Clock, AlertCircle, Sparkles, Map, ArrowRight,
} from 'lucide-react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

// ── Vista Ejecutiva del CEO Cliente ─────────────────────────────────────
// Dashboard de alto nivel para el CEO/contraparte del cliente. NO muestra
// drill-down de Unidades de Negocio ni tareas operativas. Solo lo que un
// CEO querría revisar antes de un comité.
//
// Secciones:
//   1. Header (info del proyecto + progreso global)
//   2. Cards de avance por macroproceso (Estratégico / Táctico / Ejecución)
//   3. Mercados priorizados (matriz de mercados aprobada)
//   4. Objetivos estratégicos
//   5. Pilares estratégicos
//   6. KPIs del Cuadro de Mando
//   7. Actividad reciente (últimos 30 días)
//   8. Archivos del proyecto (descargables)
//   9. Comentarios del CEO
// ────────────────────────────────────────────────────────────────────────

const STATUS_BADGE = {
  pending:     { label: 'Pendiente',   className: 'bg-gray-100 text-gray-600' },
  in_progress: { label: 'En progreso', className: 'bg-blue-100 text-blue-700' },
  review:      { label: 'En revisión', className: 'bg-purple-100 text-purple-700' },
  completed:   { label: 'Completado',  className: 'bg-emerald-100 text-emerald-700' },
  blocked:     { label: 'Bloqueado',   className: 'bg-red-100 text-red-700' },
}

const MACRO_LABEL = {
  strategic_plan: 'Plan Estratégico',
  tactical_plan:  'Plan Táctico',
  execution:      'Ejecución',
}

const MACRO_ACCENT = {
  strategic_plan: { bg: 'bg-brand-50',   bar: 'bg-brand-600',   text: 'text-brand-700' },
  tactical_plan:  { bg: 'bg-amber-50',   bar: 'bg-amber-500',   text: 'text-amber-700' },
  execution:      { bg: 'bg-emerald-50', bar: 'bg-emerald-600', text: 'text-emerald-700' },
}

export default function CeoDashboardPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile } = useAuth()

  const [consultancy, setConsultancy] = useState(null)
  const [phases, setPhases]           = useState([])
  const [objectives, setObjectives]   = useState([])
  const [pillars, setPillars]         = useState([])
  const [kpis, setKpis]               = useState([])
  const [markets, setMarkets]         = useState([])
  const [activity, setActivity]       = useState([])
  const [loading, setLoading]         = useState(true)

  useEffect(() => { fetchAll() }, [id])

  async function fetchAll() {
    setLoading(true)
    const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
    // Promise.allSettled para que UNA query rota (ej. columna faltante) no
    // tumbe el dashboard entero. Cada sección renderiza solo si su query OK.
    const results = await Promise.allSettled([
      supabase.from('consultancies').select('*').eq('id', id).single(),
      supabase.from('phases').select('id, name, status, progress, macro_process, business_unit_id').eq('consultancy_id', id),
      supabase.from('strategic_objectives').select('*').eq('consultancy_id', id).order('obj_order'),
      supabase.from('strategic_pillars').select('*').eq('consultancy_id', id).order('pillar_order'),
      supabase.from('kpis').select('*').eq('consultancy_id', id).order('created_at'),
      supabase.from('market_selections')
        .select('*, market:industry_markets(*), bu:business_units(name)')
        .eq('consultancy_id', id),
      supabase.from('activity_log')
        .select('*')
        .eq('consultancy_id', id)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(20),
    ])

    const pick = (idx) => results[idx]?.status === 'fulfilled' ? results[idx].value?.data : null

    setConsultancy(pick(0))
    setPhases(pick(1) ?? [])
    setObjectives(pick(2) ?? [])
    setPillars(pick(3) ?? [])
    setKpis(pick(4) ?? [])
    setMarkets(pick(5) ?? [])
    setActivity(pick(6) ?? [])

    // Log silencioso de queries que fallaron — útil para debug futuro
    results.forEach((r, i) => {
      if (r.status === 'rejected') console.warn('[CeoDashboard query failed]', i, r.reason)
    })
    setLoading(false)
  }

  // ── Computed ────────────────────────────────────────────────────────────
  const macroBuckets = (() => {
    const b = { strategic_plan: [], tactical_plan: [], execution: [] }
    for (const p of phases) {
      if (b[p.macro_process]) b[p.macro_process].push(p)
    }
    return b
  })()

  const macroProgress = Object.fromEntries(
    Object.entries(macroBuckets).map(([k, list]) => {
      if (list.length === 0) return [k, 0]
      const sum = list.reduce((a, p) => a + (p.progress ?? 0), 0)
      return [k, Math.round(sum / list.length)]
    })
  )

  const globalProgress = phases.length === 0
    ? 0
    : Math.round(phases.reduce((a, p) => a + (p.progress ?? 0), 0) / phases.length)

  const currentStage = (() => {
    // Pick the macro process with progress > 0 and < 100, else the first
    for (const k of ['execution', 'tactical_plan', 'strategic_plan']) {
      if (macroProgress[k] > 0 && macroProgress[k] < 100) return MACRO_LABEL[k]
    }
    if (macroProgress.strategic_plan === 100) return 'Plan Estratégico completado'
    return 'Plan Estratégico'
  })()

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-500 border-t-transparent" />
    </div>
  )

  if (!consultancy) return (
    <div className="card p-10 text-center text-gray-500">
      No se pudo cargar la información del proyecto.
    </div>
  )

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* ── 1. Header del proyecto ──────────────────────────── */}
      <div className="card">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-brand-700 flex items-center justify-center text-white font-bold text-xl md:text-2xl flex-shrink-0">
            {consultancy.client_company?.charAt(0) ?? 'P'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-base md:text-xl font-bold text-gray-900 leading-tight">{consultancy.name}</h1>
              <span className={`badge ${STATUS_BADGE[consultancy.status]?.className ?? 'bg-gray-100 text-gray-600'}`}>
                {STATUS_BADGE[consultancy.status]?.label ?? consultancy.status}
              </span>
            </div>
            <p className="text-gray-500 text-sm">{consultancy.client_company}</p>
            {consultancy.industry && <p className="text-xs text-gray-400 mt-0.5">{consultancy.industry}</p>}
            {consultancy.description && (
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">{consultancy.description}</p>
            )}
          </div>
        </div>

        {/* Progreso global */}
        <div className="mt-5 pt-5 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Progreso global</span>
            <span className="text-2xl font-bold text-brand-700 tabular-nums">{globalProgress}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-brand-500 to-brand-700 transition-all"
              style={{ width: `${globalProgress}%` }} />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Etapa actual: <span className="font-medium text-gray-600">{currentStage}</span>
          </p>
        </div>

        {/* CTA principal — drill in al plan completo */}
        <button
          onClick={() => navigate(`/consultancy/${id}`)}
          className="mt-5 w-full flex items-center justify-between gap-3 px-5 py-3.5 rounded-xl bg-gradient-to-r from-brand-700 to-brand-800 hover:from-brand-800 hover:to-brand-900 text-white font-semibold text-sm transition-all shadow-sm hover:shadow"
        >
          <span className="flex items-center gap-2">
            <Briefcase size={16} />
            Explorar el Plan Estratégico completo
          </span>
          <ArrowRight size={18} />
        </button>
      </div>

      {/* ── 1.5 Información necesaria (DRL inicial + final) ──────── */}
      <DRLProgressBars consultancyId={id} />

      {/* ── 2. Avance por macroproceso (clickable) ─────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {['strategic_plan', 'tactical_plan', 'execution'].map(macro => {
          const accent = MACRO_ACCENT[macro]
          const pct    = macroProgress[macro] ?? 0
          const count  = macroBuckets[macro].length
          return (
            <button
              key={macro}
              type="button"
              onClick={() => navigate(`/consultancy/${id}`)}
              className="card text-left hover:border-brand-300 hover:shadow-md transition-all group cursor-pointer"
              title={`Ir a ${MACRO_LABEL[macro]}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-9 h-9 rounded-xl ${accent.bg} flex items-center justify-center`}>
                  {macro === 'strategic_plan' && <Target size={18} className={accent.text} />}
                  {macro === 'tactical_plan'  && <Compass size={18} className={accent.text} />}
                  {macro === 'execution'      && <TrendingUp size={18} className={accent.text} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">{MACRO_LABEL[macro]}</p>
                  <p className="text-xs text-gray-400">{count} fase{count !== 1 ? 's' : ''}</p>
                </div>
                <span className={`text-xl font-bold ${accent.text} tabular-nums`}>{pct}%</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full ${accent.bar} transition-all`} style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-3 flex items-center justify-end gap-1 text-xs text-gray-400 group-hover:text-brand-600 transition-colors">
                Ver detalle <ArrowRight size={12} />
              </div>
            </button>
          )
        })}
      </div>

      {/* ── 3. Mercados priorizados ─────────────────────────── */}
      {markets.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Map size={18} className="text-brand-600" />
            <h2 className="text-base font-semibold text-gray-800">Mercados priorizados</h2>
            <span className="text-xs text-gray-400 ml-auto">{markets.length} mercado{markets.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {markets.map(sel => (
              <div key={sel.id} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                <div className="w-2 h-2 rounded-full bg-brand-600 mt-1.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {sel.market?.market_name ?? 'Mercado sin nombre'}
                  </p>
                  {sel.bu?.name && (
                    <p className="text-xs text-gray-500 mt-0.5">→ {sel.bu.name}</p>
                  )}
                  {sel.market?.attractiveness && (
                    <p className="text-xs text-gray-400 mt-0.5">Atractivo: {sel.market.attractiveness}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 4. Objetivos estratégicos ───────────────────────── */}
      {objectives.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Target size={18} className="text-brand-600" />
            <h2 className="text-base font-semibold text-gray-800">Objetivos estratégicos</h2>
          </div>
          <div className="space-y-3">
            {objectives.map(obj => (
              <div key={obj.id} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0 last:pb-0">
                <div className="w-7 h-7 rounded-lg bg-brand-50 text-brand-700 text-sm font-bold flex items-center justify-center flex-shrink-0">
                  {obj.obj_order + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-800">{obj.name}</p>
                    {obj.status && (
                      <span className={`badge text-[10px] flex-shrink-0 ${STATUS_BADGE[obj.status]?.className ?? 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_BADGE[obj.status]?.label ?? obj.status}
                      </span>
                    )}
                  </div>
                  {obj.description && (
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{obj.description}</p>
                  )}
                  {obj.target_date && (
                    <p className="text-[11px] text-gray-400 mt-1">Meta: {format(new Date(obj.target_date), 'd MMM yyyy', { locale: es })}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 5. Pilares estratégicos ─────────────────────────── */}
      {pillars.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={18} className="text-amber-600" />
            <h2 className="text-base font-semibold text-gray-800">Pilares estratégicos</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {pillars.map(pil => (
              <div key={pil.id} className="p-3 rounded-lg bg-amber-50/50 border border-amber-100">
                <p className="text-sm font-semibold text-gray-800">{pil.name}</p>
                {pil.description && (
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">{pil.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 6. KPIs ─────────────────────────────────────────── */}
      {kpis.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={18} className="text-emerald-600" />
            <h2 className="text-base font-semibold text-gray-800">Cuadro de Mando — KPIs</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-2 font-semibold text-gray-600 text-xs uppercase tracking-wide">Indicador</th>
                  <th className="text-right py-2 px-2 font-semibold text-gray-600 text-xs uppercase tracking-wide">Baseline</th>
                  <th className="text-right py-2 px-2 font-semibold text-gray-600 text-xs uppercase tracking-wide">Actual</th>
                  <th className="text-right py-2 px-2 font-semibold text-gray-600 text-xs uppercase tracking-wide">Meta</th>
                </tr>
              </thead>
              <tbody>
                {kpis.map(k => {
                  const baseline = Number(k.baseline_value)
                  const current  = Number(k.current_value)
                  const target   = Number(k.target_value)
                  const progress = (Number.isFinite(baseline) && Number.isFinite(target) && Number.isFinite(current) && target !== baseline)
                    ? Math.max(0, Math.min(100, ((current - baseline) / (target - baseline)) * 100))
                    : null
                  return (
                    <tr key={k.id} className="border-b border-gray-50 last:border-0">
                      <td className="py-2.5 px-2">
                        <div className="font-medium text-gray-800">{k.name}</div>
                        {k.unit && <div className="text-xs text-gray-400">{k.unit}</div>}
                      </td>
                      <td className="py-2.5 px-2 text-right tabular-nums text-gray-500">{k.baseline_value ?? '—'}</td>
                      <td className="py-2.5 px-2 text-right tabular-nums">
                        <span className="font-semibold text-gray-800">{k.current_value ?? '—'}</span>
                        {progress !== null && (
                          <div className="mt-1 h-1 w-20 ml-auto bg-gray-100 rounded overflow-hidden">
                            <div className="h-full bg-emerald-500" style={{ width: `${progress}%` }} />
                          </div>
                        )}
                      </td>
                      <td className="py-2.5 px-2 text-right tabular-nums text-gray-600">{k.target_value ?? '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── 7. Actividad reciente ───────────────────────────── */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Activity size={18} className="text-purple-600" />
          <h2 className="text-base font-semibold text-gray-800">Actividad reciente</h2>
          <span className="text-xs text-gray-400 ml-auto">Últimos 30 días</span>
        </div>
        {activity.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">Sin actividad registrada en los últimos 30 días.</p>
        ) : (
          <div className="space-y-2.5">
            {activity.slice(0, 12).map(log => (
              <div key={log.id} className="flex items-start gap-3 text-sm">
                <span className="text-base flex-shrink-0 mt-0.5">
                  {log.action === 'created'        ? '✨'
                    : log.action === 'updated'     ? '✏️'
                    : log.action === 'completed'   ? '✅'
                    : log.action === 'file_uploaded' ? '📎'
                    : log.action === 'commented'   ? '💬'
                    : '•'}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-700 leading-snug">{log.description}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {format(new Date(log.created_at), "d MMM 'a las' HH:mm", { locale: es })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── 8. Archivos del proyecto ────────────────────────── */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <FileText size={18} className="text-brand-600" />
          <h2 className="text-base font-semibold text-gray-800">Archivos del proyecto</h2>
        </div>
        <FileList entityType="consultancy" entityId={id} />
      </div>

      {/* ── 9. Comentarios del CEO ──────────────────────────── */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare size={18} className="text-purple-600" />
          <h2 className="text-base font-semibold text-gray-800">Comentarios y feedback</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Deja comentarios para el equipo de Next Valley Consultants. Los verán todos los consultores asignados al proyecto.
        </p>
        <Comments entityType="consultancy" entityId={id} />
      </div>
    </div>
  )
}
